import React, { useMemo, useState } from 'react';
import {
  Button, Input, Segmented, Slider, Space, Tag, Tooltip, Typography,
} from 'antd';
import {
  CaretRightOutlined, HolderOutlined, PauseOutlined, SearchOutlined, SoundOutlined, WarningOutlined,
} from '@ant-design/icons';
import { generateSoundList } from '../../../../../../public/board/utils/audio-strings';
import parseNumber from '../../../../../../public/board/utils/parseNumber';
import useSoundPreview from './useSoundPreview';

// Формат строки не меняем: слова через пробел, как её читают табло и веб-талон.
// Слова — имена mp3 из /api/soundlist, переменные — TNUM и WNUM0..WNUM3.
const VARIABLE_LABELS = {
  TNUM: 'номер талона',
  WNUM0: 'номер для вызова 1',
  WNUM1: 'номер для вызова 2',
  WNUM2: 'номер для вызова 3',
  WNUM3: 'номер для вызова 4',
};

const SAMPLE_TICKET = 'А015';

const VOICE_NAMES = { female: 'женской', male: 'мужской' };

// Записи у голосов разные; поле voices приходит с faq-server.
// Нет поля (старый сервер) — считаем, что запись есть.
const hasRecording = (sound, voice) => !Array.isArray(sound.voices) || sound.voices.includes(voice);

const missingStyle = { textDecoration: 'line-through', borderStyle: 'dashed', color: '#8c8c8c' };

export const splitSoundString = (value) => String(value || '').split(' ').filter(Boolean);

function SoundStringBuilder({
  data, value, onChange, callNumbers, disabled,
}) {
  // callNumbers — «Номер для вызова» конкретного места; без него (общие настройки) показываем пример
  const fromPlace = callNumbers !== undefined;
  const numbers = fromPlace ? callNumbers : '1,2,3';
  const [sounds = [], variables = []] = data || [];
  const [search, setSearch] = useState('');
  const [voice, setVoice] = useState('female');
  const [dragIndex, setDragIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const {
    playing, play, stop, volume, setVolume,
  } = useSoundPreview();

  const tokens = splitSoundString(value);
  const wnums = parseNumber(numbers);

  const variableValue = (token) => (token === 'TNUM' ? SAMPLE_TICKET : wnums[Number(token.slice(4))] || null);

  const soundsByValue = useMemo(
    () => Object.fromEntries(sounds.map((sound) => [sound.value, sound])),
    [sounds],
  );

  const tokenInfo = (token) => {
    if (VARIABLE_LABELS[token]) {
      return { kind: 'variable', label: VARIABLE_LABELS[token], value: variableValue(token) };
    }
    const sound = soundsByValue[token];
    if (!sound) return { kind: 'unknown', label: token };
    return { kind: hasRecording(sound, voice) ? 'word' : 'missing', label: sound.name };
  };
  const missingHint = `Нет записи в ${VOICE_NAMES[voice] || voice} озвучке — табло пропустит это слово`;

  const setTokens = (next) => onChange(next.join(' '));
  const addToken = (token) => setTokens([...tokens, token]);
  const removeToken = (index) => setTokens(tokens.filter((_, i) => i !== index));

  const resetDrag = () => {
    setDragIndex(null);
    setDropIndex(null);
  };

  const moveToken = () => {
    if (dragIndex === null || dropIndex === null) return;
    const next = [...tokens];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(dropIndex > dragIndex ? dropIndex - 1 : dropIndex, 0, moved);
    setTokens(next);
  };

  const showDropAt = (index) => dragIndex !== null
    && dropIndex === index && index !== dragIndex && index !== dragIndex + 1;

  const query = search.trim().toLowerCase();
  const matches = (item) => !query || item.name.toLowerCase().includes(query);
  const paletteVariables = variables.filter((item) => VARIABLE_LABELS[item.value])
    .map((item) => {
      const number = item.value !== 'TNUM' && fromPlace ? variableValue(item.value) : null;
      return { ...item, name: number ? `${VARIABLE_LABELS[item.value]} (${number})` : VARIABLE_LABELS[item.value] };
    })
    .filter(matches);
  const paletteWords = [...sounds]
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
    .filter(matches);

  // Слова могут повторяться, позиция в строке — единственный стабильный ключ
  /* eslint-disable react/no-array-index-key */
  const previewText = tokens.map((token, index) => {
    const info = tokenInfo(token);
    if (token === 'TNUM') return <b key={index}>{SAMPLE_TICKET}</b>;
    if (info.kind === 'variable') return <b key={index}>{info.value || '—'}</b>;
    if (info.kind === 'missing') return <span key={index} style={missingStyle}>{info.label}</span>;
    return <span key={index}>{info.label}</span>;
  }).reduce((acc, node, index) => (index ? [...acc, ' ', node] : [node]), []);
  /* eslint-enable react/no-array-index-key */

  const togglePlay = () => {
    if (playing) {
      stop();
      return;
    }
    play(generateSoundList(value, { number: SAMPLE_TICKET, wplace: numbers, voice }));
  };

  const unknownTokens = [...new Set(tokens.filter((token) => tokenInfo(token).kind === 'unknown'))];
  const missingLabels = [...new Set(tokens
    .map(tokenInfo)
    .filter((info) => info.kind === 'missing')
    .map((info) => info.label))];
  const tooltips = {
    unknown: 'Этого слова нет в списке звуков — табло может его не произнести',
    missing: missingHint,
  };

  const tagColor = { variable: 'purple', unknown: 'orange', word: undefined };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '6px 0',
          minHeight: 44,
          padding: 8,
          border: '1px solid #d9d9d9',
          borderRadius: 8,
        }}
        onDragOver={(e) => {
          if (dragIndex === null) return;
          e.preventDefault();
          if (e.target === e.currentTarget) setDropIndex(tokens.length);
        }}
        onDrop={(e) => {
          e.preventDefault();
          moveToken();
          resetDrag();
        }}
      >
        {tokens.length ? tokens.map((token, index) => {
          const info = tokenInfo(token);
          return (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={`${token}-${index}`}>
              {showDropAt(index) && (
              <span style={{
                width: 2, height: 22, background: '#1677ff', marginRight: 6,
              }}
              />
              )}
              <span
                draggable={!disabled}
                style={{ opacity: dragIndex === index ? 0.4 : 1, cursor: disabled ? 'default' : 'grab' }}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', String(index));
                  setDragIndex(index);
                }}
                onDragOver={(e) => {
                  if (dragIndex === null) return;
                  e.preventDefault();
                  const rect = e.currentTarget.getBoundingClientRect();
                  setDropIndex(e.clientX < rect.left + rect.width / 2 ? index : index + 1);
                }}
                onDragEnd={resetDrag}
              >
                <Tooltip title={info.kind === 'variable' && !info.value
                  ? 'В настройках рабочего места этот номер не задан — табло ничего не произнесёт'
                  : tooltips[info.kind] || ''}
                >
                  <Tag
                    color={info.kind === 'variable' && !info.value ? 'orange' : tagColor[info.kind]}
                    closable={!disabled}
                    onClose={(e) => {
                      e.preventDefault();
                      removeToken(index);
                    }}
                    style={{
                      margin: '0 6px 0 0',
                      padding: '2px 6px',
                      fontSize: 13,
                      ...(info.kind === 'missing' ? missingStyle : {}),
                    }}
                  >
                    {!disabled && <HolderOutlined style={{ color: '#bfbfbf', marginRight: 4 }} />}
                    {info.kind === 'unknown' && <WarningOutlined style={{ marginRight: 4 }} />}
                    {info.label}
                    {info.kind === 'variable' && token !== 'TNUM' && (
                      <b style={{ marginLeft: 6 }}>{`· ${info.value || 'не задан'}`}</b>
                    )}
                  </Tag>
                </Tooltip>
              </span>
            </React.Fragment>
          );
        }) : (
          <Typography.Text type="secondary">Строка пуста — добавьте слова из списка ниже</Typography.Text>
        )}
        {showDropAt(tokens.length) && <span style={{ width: 2, height: 22, background: '#1677ff' }} />}
      </div>

      {unknownTokens.length > 0 && (
        <Typography.Text type="warning" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
          {`Нет в списке звуков: ${unknownTokens.join(', ')}. Оставлено как есть, но табло может не произнести.`}
        </Typography.Text>
      )}
      {tokens.some((token) => token.startsWith('WNUM')) && (
        <Typography.Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
          {fromPlace
            ? 'Номера для вызова подставлены из настроек этого рабочего места (поле «Номер для вызова»).'
            : 'Номера для вызова здесь — пример: у каждого рабочего места они свои (поле «Номер для вызова» в его настройках).'}
        </Typography.Text>
      )}
      {missingLabels.length > 0 && (
        <Typography.Text type="warning" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
          {`В ${VOICE_NAMES[voice] || voice} озвучке нет записи: ${missingLabels.join(', ')}. Табло пропустит эти слова.`}
        </Typography.Text>
      )}

      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, padding: '8px 10px', background: '#f6f8fa', borderRadius: 6,
      }}
      >
        <Button
          type="primary"
          shape="circle"
          icon={playing ? <PauseOutlined /> : <CaretRightOutlined />}
          disabled={!tokens.length}
          onClick={togglePlay}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div>{tokens.length ? previewText : '—'}</div>
          <Space size={8} wrap>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {`Номер талона ${SAMPLE_TICKET} — пример`}
            </Typography.Text>
            <Segmented
              size="small"
              value={voice}
              onChange={setVoice}
              options={[{ label: 'жен.', value: 'female' }, { label: 'муж.', value: 'male' }]}
            />
            <Space size={4} align="center">
              <SoundOutlined style={{ color: '#8c8c8c' }} />
              <Slider
                min={0}
                max={100}
                value={volume}
                onChange={setVolume}
                tooltip={{ formatter: (v) => `${v}%` }}
                style={{ width: 100, margin: '0 4px' }}
              />
            </Space>
          </Space>
        </div>
      </div>

      {!disabled && (
        <div style={{ marginTop: 12 }}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Найти слово"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {[['Переменные', paletteVariables, 'purple'], ['Слова', paletteWords, undefined]].map(([title, list, color]) => (
            list.length > 0 && (
              <div key={title}>
                <Typography.Text
                  type="secondary"
                  style={{
                    display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', margin: '10px 0 4px',
                  }}
                >
                  {title}
                </Typography.Text>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 0' }}>
                  {list.map((item) => {
                    const missing = !hasRecording(item, voice);
                    return (
                      <Tooltip key={item.value} title={missing ? missingHint : ''}>
                        <Tag
                          color={color}
                          style={{ cursor: 'pointer', borderStyle: 'dashed', ...(missing ? missingStyle : {}) }}
                          onClick={() => addToken(item.value)}
                        >
                          {`+ ${item.name}`}
                        </Tag>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            )
          ))}
          {!paletteVariables.length && !paletteWords.length && (
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>Ничего не найдено</Typography.Text>
          )}
        </div>
      )}
    </div>
  );
}

export default SoundStringBuilder;
